import * as React from 'react';

import { Button } from '@rematter/ui-web';
import { cn } from '@rematter/ui-web/lib';
import { ArrowLeft } from 'lucide-react';

export interface TabConfig {
  key: string;
  label: string;
}

type NotificationBanner = {
  label: string | React.ReactNode;
  type: 'success' | 'failure';
} | null;

export interface DetailViewHeaderProps {
  title: string;
  subtitle?: React.ReactNode;
  titleAdornment?: React.ReactNode;
  actions?: React.ReactNode;
  onBack?: () => void;
  tabs?: TabConfig[];
  activeTab?: string;
  onTabChange?: (tabKey: string) => void;
  seamless?: boolean;
  notificationBanner?: NotificationBanner;
}

export const DetailViewHeader = ({
  title,
  subtitle,
  titleAdornment,
  actions,
  onBack,
  tabs,
  activeTab,
  onTabChange,
  seamless,
  notificationBanner,
}: DetailViewHeaderProps) => (
  <div
    className={cn(
      '@DetailViewHeader',
      'flex flex-col',
      notificationBanner?.type === 'success' && 'bg-success',
      notificationBanner?.type === 'failure' && 'bg-failure',
    )}
  >
    {/* Header */}
    {notificationBanner && (
      <div
        className={cn(
          'flex min-h-10 items-center justify-center text-sm text-white',
        )}
      >
        {notificationBanner.label}
      </div>
    )}
    <div
      className={cn(
        'bg-white p-6',
        seamless && 'bg-transparent px-2 py-4',
        notificationBanner && 'rounded-t-[9px]',
        'sticky top-0',
      )}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {!seamless && (
            <Button variant="outline" onPress={onBack}>
              <ArrowLeft className="size-4" />
            </Button>
          )}
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-semibold text-neutral/90">{title}</h1>
              {titleAdornment}
            </div>
            {subtitle && (
              <div className="flex items-center gap-1 text-sm text-neutral/50">
                {subtitle}
              </div>
            )}
          </div>
        </div>
        {actions && <div className="flex items-center gap-3">{actions}</div>}
      </div>
    </div>

    {/* Tabs */}

    {!seamless && (
      <div className="flex flex-1 flex-col">
        <div className="border-b bg-white">
          <div className="px-6">
            <div className="flex space-x-3">
              {tabs?.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => onTabChange?.(tab.key)}
                  className={cn(
                    'inline-flex items-center border-b-2 px-1 py-3 text-sm font-medium',
                    activeTab === tab.key
                      ? 'border-primary/50 text-primary/60'
                      : 'border-transparent text-neutral/50 hover:border-neutral/30 hover:text-neutral/70',
                  )}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    )}
  </div>
);
